import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../../components/dialog';
import { Button } from '../../components/button';
import { Checkbox } from '../../components/checkbox';
import { Label } from '../../components/label';
import {
  Upload,
  FileText,
  FolderOpen,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';

export interface SyncChange {
  key: string;
  variant: string;
  sku: string;
  carat_weight: number | null;
  changes: { field: string; from: unknown; to: unknown }[];
}

export interface SyncReport {
  totalRows: number;
  parsedRows: number;
  skippedRows: number;
  newCount: number;
  changedCount: number;
  unchangedCount: number;
  missingInSheetCount: number;
  duplicateKeyCount: number;
  newRows: { key: string; variant: string; sku: string; carat_weight: number | null; price: number | null }[];
  changed: SyncChange[];
  missingInSheet: { key: string; variant: string; sku: string; carat_weight: number | null }[];
  duplicateKeys: string[];
  media: {
    totalRefs: number;
    presentCount: number;
    missingRefs: string[];
    /** ref -> sha256 of the file already in storage, for spotting re-shot photos. */
    hashes?: Record<string, string>;
  };
}

export interface SyncStats extends SyncReport {
  upsertCount: number;
  deactivatedCount: number;
  errorCount: number;
  errors?: string[];
}

interface SyncCatalogModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Dry run. Writes nothing; tells us what would change and which files are missing. */
  onAnalyze: (file: File) => Promise<SyncReport>;
  /** Sends one batch of media files to Spaces. */
  onUploadMedia: (files: File[]) => Promise<{ uploadedCount: number; skippedCount: number; failed?: string[] }>;
  onApply: (file: File, deactivateMissing: boolean) => Promise<SyncStats>;
  onSuccess?: () => void;
}

type Step = 'pick' | 'review' | 'result';

/** "EA00001_2.5mm_white_1.PNG" -> "ea00001_2.5mm_white_1", the sheet's ref. */
const refOf = (filename: string) =>
  filename.replace(/\.[^.]+$/, '').trim().toLowerCase();

const sha256Hex = async (file: File) => {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(digest), b => b.toString(16).padStart(2, '0')).join('');
};

const MAX_FILES_PER_BATCH = 10;
const MAX_BYTES_PER_BATCH = 100 * 1024 * 1024;

/** One 55MB video gets its own request; small photos travel ten at a time. */
const batchFiles = (files: File[]) => {
  const batches: File[][] = [];
  let current: File[] = [];
  let bytes = 0;
  for (const file of files) {
    if (current.length > 0 && (current.length >= MAX_FILES_PER_BATCH || bytes + file.size > MAX_BYTES_PER_BATCH)) {
      batches.push(current);
      current = [];
      bytes = 0;
    }
    current.push(file);
    bytes += file.size;
  }
  if (current.length > 0) batches.push(current);
  return batches;
};

const formatValue = (value: unknown) => {
  if (value === null || value === undefined || value === '') return '—';
  const text = String(value);
  return text.length > 42 ? `${text.slice(0, 42)}…` : text;
};

export const SyncCatalogModal: React.FC<SyncCatalogModalProps> = ({
  open,
  onOpenChange,
  onAnalyze,
  onUploadMedia,
  onApply,
  onSuccess
}) => {
  const [step, setStep] = useState<Step>('pick');
  const [file, setFile] = useState<File | null>(null);
  const [report, setReport] = useState<SyncReport | null>(null);
  const [mediaFiles, setMediaFiles] = useState<Map<string, File>>(new Map());
  // Files already in storage whose bytes differ from the folder's copy.
  const [changedFiles, setChangedFiles] = useState<File[]>([]);
  const [isHashing, setIsHashing] = useState(false);
  const [deactivateMissing, setDeactivateMissing] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);
  const [result, setResult] = useState<SyncStats | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  // webkitdirectory is not in React's HTMLInputElement typings, and it is the
  // only way to let the admin hand us a folder without zipping 2GB first.
  useEffect(() => {
    const input = folderInputRef.current;
    if (!input) return;
    input.setAttribute('webkitdirectory', '');
    input.setAttribute('directory', '');
  }, [step]);

  const missingRefs = report?.media.missingRefs ?? [];

  /** Which of the missing refs the picked folder actually contains. */
  const matched = useMemo(
    () =>
      missingRefs
        .map(ref => ({ ref, file: mediaFiles.get(ref.trim().toLowerCase()) }))
        .filter((m): m is { ref: string; file: File } => Boolean(m.file)),
    [missingRefs, mediaFiles]
  );

  const unmatchedRefs = missingRefs.filter(ref => !mediaFiles.has(ref.trim().toLowerCase()));
  const toUpload = [...matched.map(m => m.file), ...changedFiles];
  const hasWork = (report?.newCount ?? 0) + (report?.changedCount ?? 0) + toUpload.length > 0;
  const busy = isAnalyzing || isApplying || isHashing;

  const reset = () => {
    setStep('pick');
    setFile(null);
    setReport(null);
    setMediaFiles(new Map());
    setChangedFiles([]);
    setResult(null);
    setUploadProgress(null);
    setDeactivateMissing(false);
  };

  const handleClose = () => {
    if (busy) return;
    reset();
    onOpenChange(false);
  };

  const pickCSV = (selected?: File | null) => {
    if (!selected) return;
    if (!selected.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please choose a .csv file');
      return;
    }
    setFile(selected);
    setReport(null);
    setResult(null);
  };

  const handleAnalyze = async () => {
    if (!file) return;
    setIsAnalyzing(true);
    try {
      const next = await onAnalyze(file);
      setReport(next);
      setStep('review');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err.message || 'Could not read this sheet', {
        position: 'top-center'
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleFolder = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    const index = new Map<string, File>();
    for (const item of Array.from(fileList)) {
      // First file wins, matching how the seed script indexes the folder.
      const key = refOf(item.name);
      if (!index.has(key)) index.set(key, item);
    }
    setMediaFiles(index);
    setChangedFiles([]);

    // One file at a time: a 55MB video is held in memory only while hashed.
    setIsHashing(true);
    try {
      const changed: File[] = [];
      for (const [ref, storedHash] of Object.entries(report?.media.hashes ?? {})) {
        const local = index.get(ref.trim().toLowerCase());
        if (local && (await sha256Hex(local)) !== storedHash) changed.push(local);
      }
      setChangedFiles(changed);
    } catch {
      toast.error('Could not compare folder files with storage; only missing files will upload', {
        position: 'top-center'
      });
    } finally {
      setIsHashing(false);
    }
  };

  const handleApply = async () => {
    if (!file) return;
    setIsApplying(true);
    try {
      const batches = batchFiles(toUpload);
      if (batches.length > 0) {
        setUploadProgress({ done: 0, total: toUpload.length });
        let done = 0;
        for (const batch of batches) {
          const res = await onUploadMedia(batch);
          if (res.failed && res.failed.length > 0) {
            toast.error(`${res.failed.length} file(s) failed to upload`, { position: 'top-center' });
          }
          done += batch.length;
          setUploadProgress({ done, total: toUpload.length });
        }
      }

      const stats = await onApply(file, deactivateMissing);
      setResult(stats);
      setStep('result');
      toast.success(`${stats.upsertCount} variant(s) written`, { position: 'top-center' });
      onSuccess?.();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err.message || 'Sync failed', {
        position: 'top-center'
      });
    } finally {
      setIsApplying(false);
      setUploadProgress(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md sm:max-w-2xl p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-heading6 font-semibold">
            <Upload className="h-5 w-5 text-primary" />
            Import &amp; Sync Catalog Sheet
          </DialogTitle>
        </DialogHeader>

        {/* ---------------------------------------------------------- pick */}
        {step === 'pick' && (
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Upload the weekly sheet. Nothing is written yet — the next screen shows exactly
              what would change and which images are still missing.
            </p>

            <div
              onDragOver={e => e.preventDefault()}
              onDrop={e => {
                e.preventDefault();
                pickCSV(e.dataTransfer.files?.[0]);
              }}
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-border hover:border-primary/50 bg-muted/20 hover:bg-muted/30 transition-all rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer text-center"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={e => pickCSV(e.target.files?.[0])}
              />
              <FileText className="h-10 w-10 text-muted-foreground mb-2" />
              {file ? (
                <div>
                  <p className="text-sm font-medium text-foreground">{file.name}</p>
                  <p className="mt-1 text-body2 text-muted-foreground">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              ) : (
                <div>
                  <p className="text-sm font-medium text-foreground">
                    Click to choose or drag &amp; drop the CSV here
                  </p>
                  <p className="mt-1 text-body2 text-muted-foreground">
                    Shopify Catalog CSV export
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* -------------------------------------------------------- review */}
        {step === 'review' && report && (
          <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-4 gap-2 rounded-lg border border-border bg-muted/30 p-3 text-center">
              <div>
                <span className="block text-caption2 text-muted-foreground">New</span>
                <span className="text-body2 font-semibold">{report.newCount}</span>
              </div>
              <div>
                <span className="block text-caption2 text-muted-foreground">Updated</span>
                <span className="text-body2 font-semibold">{report.changedCount}</span>
              </div>
              <div>
                <span className="block text-caption2 text-muted-foreground">Unchanged</span>
                <span className="text-body2 font-semibold">{report.unchangedCount}</span>
              </div>
              <div>
                <span className="block text-caption2 text-muted-foreground">Not in sheet</span>
                <span className="text-body2 font-semibold">{report.missingInSheetCount}</span>
              </div>
            </div>

            {report.duplicateKeyCount > 0 && (
              <p className="rounded-md border border-border p-2 text-caption2 text-destructive">
                {report.duplicateKeyCount} row(s) repeat a variant + carat already in this sheet and
                will be skipped: {report.duplicateKeys.slice(0, 3).join(', ')}
              </p>
            )}

            {report.changed.length > 0 && (
              <div className="rounded-lg border border-border">
                <p className="border-b border-border px-3 py-2 text-body2 font-medium">
                  Changes ({report.changedCount})
                </p>
                <ul className="max-h-48 divide-y divide-border overflow-y-auto">
                  {report.changed.map(item => (
                    <li key={item.key} className="px-3 py-2 text-caption2">
                      <span className="font-medium">{item.variant}</span>
                      {item.carat_weight !== null && (
                        <span className="text-muted-foreground"> · {item.carat_weight} ct</span>
                      )}
                      <ul className="mt-1 space-y-0.5 text-muted-foreground">
                        {item.changes.map(change => (
                          <li key={change.field}>
                            {change.field}: {formatValue(change.from)} → {formatValue(change.to)}
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
                {report.changedCount > report.changed.length && (
                  <p className="px-3 py-2 text-caption2 text-muted-foreground">
                    +{report.changedCount - report.changed.length} more
                  </p>
                )}
              </div>
            )}

            {report.newCount > 0 && (
              <div className="rounded-lg border border-border">
                <p className="border-b border-border px-3 py-2 text-body2 font-medium">
                  New variants ({report.newCount})
                </p>
                <ul className="max-h-32 divide-y divide-border overflow-y-auto">
                  {report.newRows.map(item => (
                    <li key={item.key} className="px-3 py-1.5 text-caption2">
                      <span className="font-medium">{item.variant}</span>
                      <span className="text-muted-foreground">
                        {item.carat_weight !== null ? ` · ${item.carat_weight} ct` : ''}
                        {item.price !== null ? ` · ${item.price.toLocaleString('en-US')}` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Media step: only the files Spaces does not already have. */}
            <div className="space-y-2 rounded-lg border border-border p-3">
              <p className="text-body2 font-medium">
                Images &amp; video
                <span className="ml-2 font-normal text-muted-foreground">
                  {report.media.presentCount} of {report.media.totalRefs} already uploaded as webp/mp4
                </span>
              </p>

              {report.media.totalRefs > 0 && (
                <>
                  <p className="text-caption2 text-muted-foreground">
                    {missingRefs.length > 0
                      ? `${missingRefs.length} file(s) missing or not yet converted to webp. Pick your media folder`
                      : 'Every file is in storage. Pick your media folder only if photos were re-shot'}
                    {' '}— only missing or changed files are sent, the rest stays on your machine.
                  </p>
                  <input
                    ref={folderInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={e => handleFolder(e.target.files)}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={() => folderInputRef.current?.click()}
                    disabled={busy}
                  >
                    <FolderOpen className="h-4 w-4" />
                    {mediaFiles.size > 0 ? 'Choose a different folder' : 'Choose media folder'}
                  </Button>

                  {isHashing && (
                    <p className="flex items-center gap-1.5 text-caption2 text-muted-foreground">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Checking for changed files…
                    </p>
                  )}
                  {mediaFiles.size > 0 && !isHashing && (
                    <p className="text-caption2">
                      Matched <span className="font-medium">{matched.length}</span> of{' '}
                      {missingRefs.length} missing file(s) in a folder of {mediaFiles.size}.
                      {changedFiles.length > 0 && (
                        <>
                          {' '}
                          <span className="font-medium">{changedFiles.length}</span> changed file(s)
                          will replace the stored copy.
                        </>
                      )}
                      {unmatchedRefs.length > 0 && (
                        <span className="text-muted-foreground">
                          {' '}
                          Not found: {unmatchedRefs.slice(0, 4).join(', ')}
                          {unmatchedRefs.length > 4 ? ` +${unmatchedRefs.length - 4} more` : ''}. Those
                          rows keep the media they already have.
                        </span>
                      )}
                    </p>
                  )}
                </>
              )}
            </div>

            <div className="flex items-start space-x-3 rounded-md border border-border/60 bg-muted/40 p-3">
              <Checkbox
                id="deactivateMissing"
                checked={deactivateMissing}
                onCheckedChange={c => setDeactivateMissing(!!c)}
              />
              <div className="grid gap-1.5 leading-none">
                <Label htmlFor="deactivateMissing" className="cursor-pointer text-body2 font-medium">
                  Deactivate the {report.missingInSheetCount} variant(s) not in this sheet
                </Label>
                <p className="text-caption2 text-muted-foreground">
                  Marked inactive, never deleted. Share links keep working.
                </p>
              </div>
            </div>

            {uploadProgress && (
              <div className="space-y-1">
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full bg-primary transition-all"
                    style={{ width: `${Math.round((uploadProgress.done / uploadProgress.total) * 100)}%` }}
                  />
                </div>
                <p className="text-caption2 text-muted-foreground">
                  Uploading media {uploadProgress.done}/{uploadProgress.total}
                </p>
              </div>
            )}
          </div>
        )}

        {/* -------------------------------------------------------- result */}
        {step === 'result' && result && (
          <div className="space-y-4 py-4">
            <div className="flex flex-col items-center gap-2 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-foreground">
                {result.errorCount > 0 ? (
                  <AlertTriangle className="h-6 w-6 text-destructive" />
                ) : (
                  <CheckCircle2 className="h-6 w-6" />
                )}
              </div>
              <h3 className="text-heading6 font-semibold">
                {result.errorCount > 0 ? 'Sync finished with errors' : 'Sync complete'}
              </h3>
            </div>

            <div className="grid grid-cols-4 gap-2 rounded-lg border border-border bg-muted/30 p-3 text-center">
              <div>
                <span className="block text-caption2 text-muted-foreground">Written</span>
                <span className="text-body2 font-semibold">{result.upsertCount}</span>
              </div>
              <div>
                <span className="block text-caption2 text-muted-foreground">Unchanged</span>
                <span className="text-body2 font-semibold">{result.unchangedCount}</span>
              </div>
              <div>
                <span className="block text-caption2 text-muted-foreground">Deactivated</span>
                <span className="text-body2 font-semibold">{result.deactivatedCount}</span>
              </div>
              <div>
                <span className="block text-caption2 text-muted-foreground">Errors</span>
                <span
                  className={`text-body2 font-semibold ${result.errorCount > 0 ? 'text-destructive' : ''}`}
                >
                  {result.errorCount}
                </span>
              </div>
            </div>

            {result.errors && result.errors.length > 0 && (
              <div className="space-y-1 rounded-lg border border-border p-3">
                <p className="text-body2 font-medium">Rows that failed</p>
                <ul className="max-h-32 space-y-0.5 overflow-y-auto text-caption2 text-muted-foreground">
                  {result.errors.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              </div>
            )}

            {result.media.missingRefs.length > 0 && (
              <p className="text-caption2 text-muted-foreground">
                {result.media.missingRefs.length} media file(s) were still missing at import time, so
                those rows kept whatever images they already had.
              </p>
            )}
          </div>
        )}

        <DialogFooter className="flex justify-between items-center sm:justify-end gap-2 pt-2">
          {step === 'pick' && (
            <>
              <Button variant="outline" onClick={handleClose} disabled={busy}>
                Cancel
              </Button>
              <Button onClick={handleAnalyze} disabled={!file || busy} className="gap-2">
                {isAnalyzing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Checking sheet…
                  </>
                ) : (
                  'Check changes'
                )}
              </Button>
            </>
          )}

          {step === 'review' && (
            <>
              <Button
                variant="outline"
                onClick={() => setStep('pick')}
                disabled={busy}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <Button onClick={handleApply} disabled={busy || !hasWork} className="gap-2">
                {isApplying ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {uploadProgress ? 'Uploading media…' : 'Applying…'}
                  </>
                ) : hasWork ? (
                  <>
                    <Upload className="h-4 w-4" />
                    {toUpload.length > 0
                      ? `Upload ${toUpload.length} file(s) & apply`
                      : 'Apply changes'}
                  </>
                ) : (
                  'Nothing to apply'
                )}
              </Button>
            </>
          )}

          {step === 'result' && <Button onClick={handleClose}>Done</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SyncCatalogModal;
